import type { ChangeEvent } from 'react';

/**
 * Shared utility for generating unique review image IDs (used by both webapp and mobile)
 */
export const generateReviewImageId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export interface ReviewImageFile extends File {
  id: string;
}

export const createReviewImageFile = (file: File): ReviewImageFile => {
  const reviewImageFile = file as ReviewImageFile;
  reviewImageFile.id = generateReviewImageId();
  return reviewImageFile;
};

export interface ReviewImagePreview {
  id: string;
  url: string;
}

export const createImagePreviews = (images: ReviewImageFile[]): ReviewImagePreview[] => {
  return images.map((file) => ({
    id: file.id,
    url: URL.createObjectURL(file),
  }));
};

export const handleImageUpload = (
  e: ChangeEvent<HTMLInputElement>,
  currentImages: ReviewImageFile[],
  setImages: (images: ReviewImageFile[]) => void,
  maxImages = 5,
) => {
  const files = e.target.files;
  if (!files) {
    return;
  }

  const fileArray = Array.from(files).map(createReviewImageFile);
  const newImages = [...currentImages, ...fileArray];

  const limitedImages = newImages.slice(0, maxImages);
  setImages(limitedImages);
  e.target.value = '';
};

export const removeImageById = (
  id: string,
  currentImages: ReviewImageFile[],
  setImages: (images: ReviewImageFile[]) => void,
) => {
  const newImages = currentImages.filter((img) => img.id !== id);
  setImages(newImages);
};

/** @deprecated Use removeImageById instead for proper React key handling */
export const removeImage = (index: number, currentImages: File[], setImages: (images: File[]) => void) => {
  const newImages = currentImages.filter((_, i) => i !== index);
  setImages(newImages);
};

export const cleanupImagePreviews = (previews: string[]) => {
  previews.forEach((url) => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};

export const validateImageFiles = (files: FileList | null): { valid: boolean; errors: string[] } => {
  if (!files) {
    return { valid: true, errors: [] };
  }

  const errors: string[] = [];
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_FILE_SIZE_10MB = 10 * 1024 * 1024;

  Array.from(files).forEach((file, index) => {
    if (!validTypes.includes(file.type)) {
      errors.push(`File ${index + 1}: Only JPEG, PNG, and WebP images are allowed`);
    }

    if (file.size > MAX_FILE_SIZE_10MB) {
      errors.push(`File ${index + 1}: File size must be less than 10MB`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
